import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { GameCardMinimal } from "@/components/cards/GameCardMinimal";
import { LeagueFilter } from "@/components/filters/LeagueFilter";
import { DateNavigator } from "@/components/navigation/DateNavigator";
import { useGamesList, GameMinimal } from "@/hooks/useGamesList";
import { usePageFocus } from "@/hooks/usePageFocus";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { format, addDays, startOfDay } from "date-fns";
import { SG7_LEAGUES } from "@/lib/constants";
import { syncOdds } from "@/lib/edgeFunctions";
import { isWorldCupWindow } from "@/lib/worldCup2026";
import { WorldCupPromoCard } from "@/components/world-cup/WorldCupPromoCard";

interface GamesPaginatedListProps {
  onViewAnalysis?: (game: GameMinimal) => void;
  className?: string;
  defaultLeagueId?: number;
  showFilters?: boolean;
  defaultStatus?: string;
  controlledStatus?: string; // Externally controlled status filter
  controlledDate?: Date; // Externally controlled date
  onDateChange?: (date: Date) => void; // Date change callback
  showDateNavigator?: boolean; // Show date navigator (for desktop)
  showLeagueFilter?: boolean; // Show league filter
  favoriteFixtureIds?: number[]; // Filter by favorite fixtures
  isFavorite?: (fixtureId: number) => boolean; // Check if game is favorite
  onToggleFavorite?: (fixtureId: number) => void; // Toggle favorite
}

// Array of allowed league API IDs from SG7_LEAGUES
const ALLOWED_LEAGUE_API_IDS = SG7_LEAGUES.map(league => league.apiId);

export function GamesPaginatedList({
  onViewAnalysis,
  className,
  defaultLeagueId,
  showFilters = true,
  defaultStatus,
  controlledStatus,
  controlledDate,
  onDateChange,
  showDateNavigator = true,
  showLeagueFilter = true,
  favoriteFixtureIds,
  isFavorite,
  onToggleFavorite
}: GamesPaginatedListProps) {
  const navigate = useNavigate();
  const [selectedLeagueId, setSelectedLeagueId] = useState<number | undefined>(defaultLeagueId);
  const [internalDate, setInternalDate] = useState<Date>(new Date());
  const [syncedFixtureIds, setSyncedFixtureIds] = useState<Set<number>>(new Set());
  
  // Ref for debouncing refresh calls
  const refreshTimerRef = useRef<NodeJS.Timeout>();

  // Use controlled date if provided, otherwise use internal state
  const selectedDate = controlledDate !== undefined ? controlledDate : internalDate;

  // Use controlled status if provided, otherwise use default
  const effectiveStatus = controlledStatus !== undefined ? controlledStatus : defaultStatus;

  // Handle date change
  const handleDateChange = (date: Date) => {
    if (onDateChange) {
      onDateChange(date);
    } else {
      setInternalDate(date);
    }
  };

  // Calculate date range for single day
  const getDateRange = () => {
    const startDate = startOfDay(selectedDate);
    // Single day: [startOfDay, startOfNextDay) - semi-open interval
    return {
      dateFrom: format(startDate, 'yyyy-MM-dd'),
      dateTo: format(addDays(startDate, 1), 'yyyy-MM-dd')
    };
  };

  const dateRange = getDateRange();

  const {
    games,
    loading,
    hasMore,
    total,
    loadMore,
    refresh
  } = useGamesList({
    leagueId: selectedLeagueId,
    status: effectiveStatus,
    ...(favoriteFixtureIds || effectiveStatus === 'live' ? {} : dateRange),
    limit: 100,
    favoriteFixtureIds
  });

  // Sync selectedLeagueId with defaultLeagueId prop changes
  useEffect(() => {
    setSelectedLeagueId(defaultLeagueId);
  }, [defaultLeagueId]);

  // Reset synced fixtures when filters change
  // The useGamesList hook already handles auto-refresh with proper debouncing
  useEffect(() => {
    setSyncedFixtureIds(new Set());
  }, [selectedLeagueId, effectiveStatus, selectedDate]);

  // Auto-refresh on page focus and network reconnection
  usePageFocus({ 
    onFocus: () => {
      console.log('Page focused - refreshing games');
      refresh();
    }
  });
  
  useNetworkStatus({
    onReconnect: () => {
      console.log('Network reconnected - refreshing games');
      refresh();
    }
  });

  // Auto-sync odds for games without odds (SG7 leagues only)
  useEffect(() => {
    const syncOddsForGames = async () => {
      // Filter: only SG7 leagues + scheduled/live + missing odds + not already synced
      const gamesNeedingOdds = games.filter(game => 
        ALLOWED_LEAGUE_API_IDS.includes(game.league_api_id) &&
        (game.status === 'scheduled' || game.status === 'live') && 
        (!game.home_odds || !game.draw_odds || !game.away_odds) &&
        !syncedFixtureIds.has(game.fixture_id) // Skip already synced games
      );
      
      if (gamesNeedingOdds.length > 0) {
        console.log(`Syncing odds for ${gamesNeedingOdds.length} SG7 league games...`);
        const fixtureIds = gamesNeedingOdds.map(g => g.fixture_id);
        
        // Batch requests in chunks of 20 (API limit)
        const BATCH_SIZE = 20;
        const batches = [];
        for (let i = 0; i < fixtureIds.length; i += BATCH_SIZE) {
          batches.push(fixtureIds.slice(i, i + BATCH_SIZE));
        }
        
        console.log(`Batching into ${batches.length} requests (${BATCH_SIZE} fixtures each)`);
        
        try {
          // Process batches sequentially to avoid overwhelming the API
          for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];
            console.log(`Processing batch ${i + 1}/${batches.length} (${batch.length} fixtures)`);
            await syncOdds(batch);
            
            // Mark these games as synced
            setSyncedFixtureIds(prev => {
              const newSet = new Set(prev);
              batch.forEach(id => newSet.add(id));
              return newSet;
            });
          }
          
          console.log('✅ Odds sync completed for all batches');
          
          // The data will be updated automatically via Supabase realtime or next fetch
          // No need to call refresh() which causes the loop
        } catch (error) {
          console.error('❌ Failed to sync odds:', error);
        }
      }
    };
    
    if (games.length > 0 && !loading) {
      syncOddsForGames();
    }
  }, [games, loading, syncedFixtureIds]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      loadMore();
    }
  };

  const handleCardClick = (game: GameMinimal) => {
    navigate(`/game/${game.fixture_id}`);
  };

  return (
    <div className={cn("space-y-6", className)}>
      {/* Filters */}
      {showFilters && (
        <div className="space-y-4">
          {/* League Filter */}
          {showLeagueFilter && (
            <div className="flex justify-center">
              <LeagueFilter
                selectedLeagueId={selectedLeagueId}
                onLeagueChange={setSelectedLeagueId}
                variant="dropdown"
              />
            </div>
          )}

          {/* Date Navigator - Desktop Only */}
          {showDateNavigator && (
            <DateNavigator
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              leagueId={selectedLeagueId}
            />
          )}
        </div>
      )}

      {/* Games Grid */}
      {loading && games.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : games.length === 0 ? (
        <div className="py-12">
          {effectiveStatus !== 'live' && isWorldCupWindow() ? (
            <WorldCupPromoCard />
          ) : (
            <div className="text-center">
              <div className="sketch-border bg-card p-8 max-w-md mx-auto">
                <Calendar size={48} className="mx-auto mb-4 text-gray-medium" />
                <h3 className="font-bold text-lg mb-2">
                  {effectiveStatus === 'live'
                    ? '🔴 No live fixtures right now'
                    : 'No fixtures found'
                  }
                </h3>
                <p className="text-gray-medium text-sm">
                  {effectiveStatus === 'live'
                    ? 'No matches in progress. Check back later!'
                    : 'Try adjusting filters or select a different league.'
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-4 animate-in fade-in duration-300">
          {games.map((game) => (
            <GameCardMinimal
              key={game.id}
              game={game}
              onViewAnalysis={onViewAnalysis}
              onCardClick={handleCardClick}
              isFavorite={isFavorite ? isFavorite(game.fixture_id) : false}
              onToggleFavorite={onToggleFavorite}
            />
          ))}
        </div>
      )}

      {/* Load More Button */}
      {hasMore && games.length > 0 && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={handleLoadMore}
            disabled={loading}
            className="sketch-border hover-sketch font-bold"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Carregando...
              </>
            ) : (
              'Load More Fixtures'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}